import AddDoctor from '@/pages/AddEditDoctor';
import { EditDoctor } from '@/pages/AddEditDoctor/components';
import { EditAvailability } from '@/pages/DoctorManagementPage/components/EditAvailability';
import { ManageAvailability } from '@/pages/DoctorManagementPage/components/ManageAvailability';
import QAIssue from '@/pages/QA-auditFlow/QAIssue';
import QAListPage from '@/pages/QA-auditFlow/QAListPage';
import Supervisor from '@/pages/Supervisor';
import EscalationIssue from '@/pages/Supervisor/components/EscalationIssue';
import EscalationListPage from '@/pages/Supervisor/components/EscalationListPage';
import React from 'react';
import { RouteObject } from 'react-router-dom';
import AddUser from '@/pages/UserManagement/AddUser';
import ProtectedRoute from '@/components/Authenticate/ProtectedRoute';
import { newPermissionsFeatureCodes, permissionsFeatureCodes } from '@/constants';

/** Route **/
export const ROUTE = {
  HOME: '/',
  REVIEW_PAUSED: '/review-paused',
  VALIDATE_PAUSED: '/validate-paused',
  DOCTOR_MANAGEMENT: '/doctor-management',
  DOCUMENT_REVIEW: '/document-review',
  EMPTY_SCREEN: '/document-not-found',
  USER_MANAGEMENT: '/user-management',
  USER_PROFILE: '/user-management/user-profile',
  VALIDATE_REPORT: 'validate-report',
  VALIDATE_REPORT_REVIEW: 'validate-report/review',
  ADD_DOCTOR: '/doctor-management/add-doctor',
  ADD_USER: '/user-management/add-user',
  DOCTOR_PROFILE_MANAGEMENT: 'profile-management',
  DOCTOR_MANAGE_AVAILABILITY: 'manage-appointment',
  DOCTOR_MANAGE_SCHEDULE: 'manage-schedule',
  SUPERVISOR: '/supervisor',
  SUPERVISOR_ESCALATION: '/supervisor/escalation',
  QA_AUDIT_FLOW: '/qa-audit-flow',
  AUDIT_LOG: '/audit-log',
};

/** Page Components **/
const LandingPage = React.lazy(() => import('@/pages/LandingPage'));
const PauseReviewPage = React.lazy(() => import('@/pages/PauseScreen'));
const DoctorManagementPage = React.lazy(
  () => import('@/pages/DoctorManagementPage')
);
const DoctorList = React.lazy(() => import('@/pages/DoctorList'));
const DocumentReviewPage = React.lazy(
  () => import('@/pages/DocumentReviewPage')
);
const UserManagementPage = React.lazy(
  () => import('@/pages/UserManagement/UserList')
);
const EmptyStateScreen = React.lazy(() => import('@/pages/EmptyStateScreen'));
const ValidateReport = React.lazy(() => import('@/pages/ValidateReport/index'));
const ValidateReportLanding = React.lazy(() => import('@/pages/ValidateReport/LandingPage'));
const AuditLog = React.lazy(() => import('@/pages/AuditLog/AuditLogListPage'));

const protectedRoutes: RouteObject[] = [
  {
    path: ROUTE.HOME, element: <LandingPage />
  },
  {
    path: ROUTE.USER_MANAGEMENT,
    children: [
      {
        index: true, element:
          <ProtectedRoute permissionKeys={[
            newPermissionsFeatureCodes.user_sa_pa_create,
            newPermissionsFeatureCodes.user_fa_create,
            newPermissionsFeatureCodes.user_records_management_create,
          ]}><UserManagementPage /></ProtectedRoute>
      },
      {
        path: ':id/user-profile', element:
          <ProtectedRoute permissionKeys={[
            newPermissionsFeatureCodes.user_sa_pa_modify,
            newPermissionsFeatureCodes.user_fa_modify,
            newPermissionsFeatureCodes.user_records_management_modify,
          ]}><AddUser /></ProtectedRoute>
      },
    ],
  },
  {
    path: ROUTE.ADD_USER, element:
      <ProtectedRoute permissionKeys={[
        newPermissionsFeatureCodes.user_sa_pa_create,
        newPermissionsFeatureCodes.user_fa_create,
        newPermissionsFeatureCodes.user_records_management_create,
      ]}><AddUser /></ProtectedRoute>
  },
  {
    path: ROUTE.DOCTOR_MANAGEMENT,
    element:
      <ProtectedRoute permissionKeys={[permissionsFeatureCodes.doctor_profile_create]}><DoctorList /></ProtectedRoute>,
  },
  {
    path: `${ROUTE.DOCTOR_MANAGEMENT}/:id`,
    element:
      <ProtectedRoute permissionKeys={[permissionsFeatureCodes.doctor_profile_modify]}><DoctorManagementPage /></ProtectedRoute>,
    children: [
      {
        index: true, element: <EditDoctor />
      },
      {
        path: ROUTE.DOCTOR_PROFILE_MANAGEMENT, element: <EditDoctor />
      },
      {
        path: ROUTE.DOCTOR_MANAGE_AVAILABILITY,
        element: <ManageAvailability />
      },
      {
        path: ROUTE.DOCTOR_MANAGE_SCHEDULE, element: <EditAvailability />
      },
    ],
  },
  {
    path: ROUTE.ADD_DOCTOR,
    element:
      <ProtectedRoute permissionKeys={[permissionsFeatureCodes.doctor_profile_create]}><AddDoctor /></ProtectedRoute>,
  },
  { path: ROUTE.REVIEW_PAUSED, element: <PauseReviewPage /> },
  { path: ROUTE.VALIDATE_PAUSED, element: <PauseReviewPage /> },
  {
    path: ROUTE.DOCUMENT_REVIEW, element:
      <ProtectedRoute permissionKeys={[newPermissionsFeatureCodes.reviewer_documents_view]}><DocumentReviewPage /></ProtectedRoute>
  },
  {
    path: ROUTE.VALIDATE_REPORT, element:
      <ProtectedRoute permissionKeys={[newPermissionsFeatureCodes.validator_documents_view]}><ValidateReportLanding /></ProtectedRoute>,
  },
  {
    path: ROUTE.VALIDATE_REPORT_REVIEW, element:
      <ProtectedRoute permissionKeys={[newPermissionsFeatureCodes.validator_documents_view]}><ValidateReport /></ProtectedRoute>,
  },
  {
    path: ROUTE.SUPERVISOR,
    element:
      <ProtectedRoute permissionKeys={[newPermissionsFeatureCodes.escalated_documents_view]}><Supervisor /></ProtectedRoute>,
  },
  {
    path: ROUTE.SUPERVISOR_ESCALATION,
    element:
      <ProtectedRoute permissionKeys={[permissionsFeatureCodes.escalated_documents_view]}><EscalationListPage /></ProtectedRoute>,
  },
  {
    path: `${ROUTE.SUPERVISOR_ESCALATION}/:ticketNo`,
    element:
      <ProtectedRoute permissionKeys={[newPermissionsFeatureCodes.escalated_documents_modify]}><EscalationIssue /></ProtectedRoute>,
  },
  {
    path: ROUTE.QA_AUDIT_FLOW,
    element:
      <ProtectedRoute permissionKeys={[newPermissionsFeatureCodes.quality_check_summary]}><QAListPage /></ProtectedRoute>,
  },
  {
    path: `${ROUTE.QA_AUDIT_FLOW}/:ticketNo`,
    element:
      <ProtectedRoute permissionKeys={[newPermissionsFeatureCodes.quality_check_conduct]}><QAIssue /></ProtectedRoute>,
  },
  {
    path: ROUTE.EMPTY_SCREEN, element: <EmptyStateScreen message={''} />
  },
  {
    path: ROUTE.AUDIT_LOG, element:
      <ProtectedRoute permissionKeys={[newPermissionsFeatureCodes.logs_view]}><AuditLog /></ProtectedRoute>
  },
];

export {
  protectedRoutes,
};
